import { FC } from 'react';

import { Box, BoxProps, Grid, Text, keyframes } from '@chakra-ui/react';
import { Card } from 'models';
import getInitials from 'utils/getInitials';

import UserIcon from 'icons/user.svg';

type LeaderboardCardPlaceholderProps = {
  card?: Card;
  showAnimation?: boolean;
  noName?: boolean;
  hidePoints?: boolean;
} & BoxProps;

const onLoadAnimation = keyframes`
  0% {
    scale: 1;
    opacity: 1;
  }
  99% {
    scale: 1.5;
    opacity: 0.3;
  }
  100% {
    display: none;
  }
`;

const LeaderboardCardPlaceholder: FC<LeaderboardCardPlaceholderProps> = ({
  card,
  showAnimation,
  noName,
  hidePoints,
  ...props
}) => {
  const headshot =
    card?.headshotThumbnails?.small ?? card?.headshotThumbnails?.medium ?? card?.headshotThumbnails?.original ?? '';

  return (
    <Grid
      w="60px"
      gridTemplateColumns="1fr"
      gridTemplateRows={noName && !card?.playerName ? '1fr' : '1fr 11px'}
      gap="4px"
    >
      <Box
        w="60px"
        h="60px"
        backgroundColor={card ? (card.status === 'bench' ? '#343434' : 'white') : '#343434'}
        borderRadius="16px"
        backgroundImage={card ? `url(${headshot})` : `url(${UserIcon})`}
        backgroundSize={card ? 'cover' : '22px'}
        backgroundRepeat="no-repeat"
        backgroundPosition="center center"
        border={
          card
            ? card.status === 'bench'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : 'none'
            : '1px solid rgba(255, 255, 255, 0.1)'
        }
        position="relative"
        {...props}
      >
        {card?.totalPoints !== 0 && !hidePoints && (
          <Box
            position="absolute"
            top="-6px"
            right="-6px"
            fontSize="11px"
            lineHeight="1"
            p="4px 5px"
            backgroundColor="#242424"
            borderRadius="16px"
          >
            {card?.totalPoints}
          </Box>
        )}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0"
          backgroundColor="rgba(255, 255, 255, 0.5)"
          borderRadius="14px"
          animation={headshot && showAnimation ? `${onLoadAnimation} 0.3s ease-in-out forwards` : ''}
        />
        {card?.playerInjuries && (
          <Box
            position="absolute"
            top="-7px"
            right="-5px"
            w="20px"
            h="20px"
            color="rgba(36, 36, 36, 1)"
            fontSize="8px"
            lineHeight="1"
            fontWeight="bold"
            fontFamily="JetBrains Mono"
            display="flex"
            textTransform="uppercase"
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            backgroundColor="rgba(255, 165, 0, 1)"
          >
            gtd
          </Box>
        )}
      </Box>
      {card && card.playerName && (
        <Box w="100%" textAlign="center" overflow="hidden" maxW="100%" px="2px">
          <Text
            color="white"
            opacity="0.6"
            fontSize="9px"
            fontWeight="regular"
            lineHeight="1.2"
            textTransform="capitalize"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxW="100%"
          >
            {getInitials(card.playerName)}
          </Text>
        </Box>
      )}
    </Grid>
  );
};

export default LeaderboardCardPlaceholder;
